import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { get, isNumber } from 'lodash';
import { UTILIZATION_ERROR, DATES_ERROR } from './messageConstants';
import PreviousAuthorizationRequestsDetails from './PreviousAuthorizationRequestsDetails';
import formatShortDate from './formatShortDate';
import LineItem from '../../pages/builder/LineItem';
import usePerson from '../../hooks/usePerson';
import { useFind } from '../../api/ApiHooks';
import Spend from './Spend';
import ErrorMessage from '../../common/form/ErrorMessage';
import { useFeatureFlag } from '../../utils/useFeatureFlag';
function InformationText(_ref) {
  var name = _ref.name,
    value = _ref.value,
    isLoaded = _ref.isLoaded;
  return /*#__PURE__*/React.createElement(LineItem, {
    field: name,
    fieldClassName: "col-span-1 p-0 text-dark-grey",
    valueClassName: "flex text-dark-blue",
    isLoaded: isLoaded
  }, value);
}
InformationText.propTypes = {
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.number]).isRequired,
  isLoaded: PropTypes.bool.isRequired
};
function CapsInfo(_ref2) {
  var formValues = _ref2.formValues,
    feeScheduleProgram = _ref2.feeScheduleProgram,
    isCostBased = _ref2.isCostBased,
    setReferralErrors = _ref2.setReferralErrors;
  var serviceStart = get(formValues, 'paymentAuthForm.service_start');
  var requestedStartDate = serviceStart && moment.unix(serviceStart).toISOString();
  var serviceEnd = get(formValues, 'paymentAuthForm.service_end');
  var requestedEndDate = serviceEnd && moment.unix(serviceEnd).toISOString();
  var pays5590RefactorInsuranceFilters = useFeatureFlag('pays5590RefactorInsuranceFilters', false);
  if (pays5590RefactorInsuranceFilters) {
    requestedStartDate = serviceStart && moment(requestedStartDate).add(1, 'day').toISOString();
    requestedEndDate = serviceEnd && moment(requestedEndDate).subtract(1, 'day').toISOString();
  }
  var person = usePerson();
  var _useFind = useFind('authorized_spend', {
      person: person === null || person === void 0 ? void 0 : person.id,
      fee_schedule_program: feeScheduleProgram === null || feeScheduleProgram === void 0 ? void 0 : feeScheduleProgram.id,
      requested_starts_at: requestedStartDate,
      requested_ends_at: requestedEndDate
    }, {
      queryConfig: {
        enabled: !!(person !== null && person !== void 0 && person.id && feeScheduleProgram !== null && feeScheduleProgram !== void 0 && feeScheduleProgram.id),
        placeholderData: undefined
      }
    }),
    authorizedSpendsData = _useFind.data,
    isFindAuthorizedSpendSuccess = _useFind.isSuccess;
  var _useFind2 = useFind('insurance', {
      person: person === null || person === void 0 ? void 0 : person.id,
      state: 'active',
      'plan.fee_schedules': feeScheduleProgram === null || feeScheduleProgram === void 0 ? void 0 : feeScheduleProgram.fee_schedule.id,
      enrolled_before: requestedStartDate,
      expired_after: requestedEndDate
    }, {
      queryClient: {
        enabled: !!(feeScheduleProgram !== null && feeScheduleProgram !== void 0 && feeScheduleProgram.id),
        placeholderData: undefined
      }
    }),
    insurancesData = _useFind2.data,
    isFindInsuranceSuccess = _useFind2.isSuccess;
  var insurances = get(insurancesData, 'data.data', []);
  var invalidInsuranceAuthorizationDates = !(insurances !== null && insurances !== void 0 && insurances.length);
  var unit = get(feeScheduleProgram, 'unit');
  var authorizationSpends = get(authorizedSpendsData, 'data.data', []); //todo use this on the modal
  var totalApprovedSpend = get(authorizedSpendsData, 'data.meta.total_approved_spend', 0);
  var totalRequestedSpend = get(authorizedSpendsData, 'data.meta.total_requested_spend', 0);
  var totalCap = get(authorizedSpendsData, 'data.meta.total_cap', 0);
  var capStartsAt = get(authorizedSpendsData, 'data.meta.cap_starts_at', null);
  var capEndsAt = get(authorizedSpendsData, 'data.meta.cap_ends_at', null);
  var authorizationSpendsErrors = get(authorizedSpendsData, 'data.meta.errors', []);
  var availableCap = get(authorizedSpendsData, 'data.meta.available_cap', 0);
  var formattedAvailableAmount = /*#__PURE__*/React.createElement(Spend, {
    isCostBased: isCostBased,
    amount: availableCap,
    unit: unit
  });
  var showClientUtilizationError = authorizationSpendsErrors.some(function (e) {
    return e.title === UTILIZATION_ERROR;
  });
  var showPassthroughDatesError = authorizationSpendsErrors.some(function (e) {
    return e.title === DATES_ERROR;
  });
  var startDate = moment(capStartsAt).utc().startOf('day');
  var endDate = moment(capEndsAt).utc().endOf('day');
  var hasCap = feeScheduleProgram === null || feeScheduleProgram === void 0 ? void 0 : feeScheduleProgram.has_cap;
  var formattedCap = hasCap ? /*#__PURE__*/React.createElement(Spend, {
    amount: totalCap,
    unit: unit,
    isCostBased: isCostBased
  }) : 'No Cap';
  var formattedTotalApprovedSpend = /*#__PURE__*/React.createElement(Spend, {
    isCostBased: isCostBased,
    amount: totalApprovedSpend,
    unit: unit
  });
  var formattedTotalRequestedSpend = /*#__PURE__*/React.createElement(Spend, {
    isCostBased: isCostBased,
    amount: totalRequestedSpend,
    unit: unit
  });
  var formattedCapPeriod = hasCap && !capStartsAt ? 'Lifetime' : "".concat(formatShortDate(startDate), " - ").concat(formatShortDate(endDate));
  var amountRequested = get(formValues, 'paymentAuthForm.amount_requested');
  var isAmountExceeded = isNumber(amountRequested) && hasCap && amountRequested > availableCap;
  var errors = useMemo(function () {
    return {
      service: !!showClientUtilizationError,
      passthrough_dates: !!showPassthroughDatesError,
      invalid_insurance_auth_dates: !!invalidInsuranceAuthorizationDates,
      amount_exceeded: !!isAmountExceeded
    };
  }, [showClientUtilizationError, showPassthroughDatesError, invalidInsuranceAuthorizationDates, isAmountExceeded]);
  useEffect(function () {
    setReferralErrors(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);
  return /*#__PURE__*/React.createElement("div", {
    className: "mb-12 grid grid-cols-2 gap-2",
    "data-testid": "caps-info-section"
  }, hasCap && /*#__PURE__*/React.createElement(InformationText, {
    name: /*#__PURE__*/React.createElement("span", {
      className: "font-bold"
    }, "AMOUNT AVAILABLE FOR AUTHORIZATION"),
    value: /*#__PURE__*/React.createElement("span", {
      className: "font-bold"
    }, formattedAvailableAmount),
    isLoaded: isFindAuthorizedSpendSuccess
  }), /*#__PURE__*/React.createElement(InformationText, {
    name: "Amount Currently Authorized",
    value: formattedTotalApprovedSpend,
    isLoaded: isFindAuthorizedSpendSuccess
  }), hasCap && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InformationText, {
    name: "Cap",
    value: formattedCap,
    isLoaded: isFindAuthorizedSpendSuccess
  }), (startDate === null || startDate === void 0 ? void 0 : startDate.isValid()) && /*#__PURE__*/React.createElement(InformationText, {
    name: "Cap Period",
    value: formattedCapPeriod,
    isLoaded: isFindAuthorizedSpendSuccess
  })), /*#__PURE__*/React.createElement(InformationText, {
    name: "Amount Pending Authorization",
    value: formattedTotalRequestedSpend,
    isLoaded: isFindAuthorizedSpendSuccess
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(PreviousAuthorizationRequestsDetails, {
    authorizationSpends: authorizationSpends,
    totalApprovedSpend: formattedTotalApprovedSpend,
    totalRequestedSpend: formattedTotalRequestedSpend,
    unit: unit,
    isCostBased: isCostBased,
    capInfo: {
      hasCap: hasCap,
      formattedCap: formattedCap,
      formattedCapPeriod: formattedCapPeriod,
      startDate: startDate
    },
    amountAvailable: formattedAvailableAmount
  })), isAmountExceeded && /*#__PURE__*/React.createElement("div", {
    className: "col-span-2 px-0 flex space-x-2 mt-6 mb-0"
  }, /*#__PURE__*/React.createElement(ErrorMessage, null, "The amount requested is more than the amount available for authorization. Please adjust the amount requested, or search for another program to meet the client's needs.")), showClientUtilizationError && /*#__PURE__*/React.createElement("div", {
    className: "col-span-2 px-0 flex space-x-2 mt-6 mb-0"
  }, /*#__PURE__*/React.createElement(ErrorMessage, null, "The client is already receiving this service through either a passthrough or non-passthrough service within this program group.")), showPassthroughDatesError && /*#__PURE__*/React.createElement("div", {
    className: "col-span-2 px-0 flex space-x-2 mt-6 mb-0"
  }, /*#__PURE__*/React.createElement(ErrorMessage, null, "Clients can only receive services from this program for 30 days or until their social coverage ends, whichever comes first.")), isFindInsuranceSuccess && isFindAuthorizedSpendSuccess && invalidInsuranceAuthorizationDates && /*#__PURE__*/React.createElement("div", {
    className: "col-span-2 px-0 flex space-x-2 mt-6 mb-0"
  }, /*#__PURE__*/React.createElement(ErrorMessage, null, "The service delivery dates requested fall outside the client's enrollment dates for all enrolled plans. Please edit the service delivery dates.")));
}
CapsInfo.propTypes = {
  formValues: PropTypes.object.isRequired,
  feeScheduleProgram: PropTypes.object.isRequired,
  isCostBased: PropTypes.bool.isRequired,
  setReferralErrors: PropTypes.func.isRequired
};
export default CapsInfo;
//# sourceMappingURL=CapsInfo.js.map